import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material'
import React, { FC, useCallback, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useCreateAdditionalPhoneMutation,
  useGetAdditionalPhonesForCustomerQuery,
  useGetCustomerDetailQuery,
  useUpdateCustomerDetailMutation
} from '../../redux/api'
import { AddPhoneNumberDialog } from './AddPhoneNumberDialog'

interface Props {
  customerId: string
  number: string | undefined
  handleNumberChange: (number: string | undefined) => void
}

export const PhoneNumberSelect: FC<Props> = ({
  customerId,
  number,
  handleNumberChange
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()

  const { data: customers } = useGetCustomerDetailQuery({
    authorization: accessToken,
    shop: shopId,
    customer: [customerId]
  })

  const customer = customers && customers[0]
  const customerInformation = customer?.information
  const customerPhoneNumber = customerInformation?.phone

  useEffect(() => {
    if (customer) {
      handleNumberChange(customerPhoneNumber)
    }
  }, [customer, customerPhoneNumber, handleNumberChange])

  const { data: additionalPhones } = useGetAdditionalPhonesForCustomerQuery({
    authorization: accessToken,
    customer: customerId
  })

  const [updateCustomer] = useUpdateCustomerDetailMutation()
  const [createAdditionalPhone] = useCreateAdditionalPhoneMutation()

  const markAsPrimary = useCallback(
    async (phone: string) => {
      await updateCustomer({
        customerEntry: {
          id: customerId,
          information: {
            ...customerInformation,
            phone
          }
        },
        authorization: accessToken
      })
    },
    [accessToken, updateCustomer, customerId, customerInformation]
  )

  const checkNewNumberInAdditionalPhones = useCallback(async () => {
    let isAdditionalPhone = false
    if (additionalPhones) {
      for (const additionalPhone of additionalPhones) {
        if (additionalPhone.info?.phone === customerPhoneNumber) {
          isAdditionalPhone = true
          break
        }
      }
    }
    if (!isAdditionalPhone) {
      await createAdditionalPhone({
        authorization: accessToken,
        customerAdditionalPhoneInfo: {
          customer: customerId,
          phone: customerPhoneNumber,
          description: 'Additional Phone'
        }
      })
    }
  }, [
    accessToken,
    additionalPhones,
    createAdditionalPhone,
    customerId,
    customerPhoneNumber
  ])

  const handleChange = useCallback(
    async (event: SelectChangeEvent) => {
      if (!event.target.value) {
        handleNumberChange(undefined)
        return
      }

      await checkNewNumberInAdditionalPhones()
      await markAsPrimary(event.target.value)
      handleNumberChange(event.target.value)
    },
    [checkNewNumberInAdditionalPhones, handleNumberChange, markAsPrimary]
  )

  const [openAddPhoneNumberDialog, setOpenAddPhoneNumberDialog] =
    useState<boolean>(false)

  return (
    <>
      {customer && (
        <AddPhoneNumberDialog
          open={openAddPhoneNumberDialog}
          setOpen={setOpenAddPhoneNumberDialog}
          customer={customer} // cannot use pound symbol for customer props because it takes some time to load
        />
      )}
      <FormControl fullWidth variant="filled" size="small">
        <InputLabel id="selectPhoneNumber">Select Phone Number</InputLabel>
        <Select
          label="selectPhoneNumber"
          value={customerPhoneNumber || number}
          onChange={handleChange}
          fullWidth>
          <MenuItem value={customerPhoneNumber} key={customerPhoneNumber}>
            {customerPhoneNumber}
          </MenuItem>
          {additionalPhones
            ?.filter(
              (additionalPhone) =>
                additionalPhone.info?.phone !== customerPhoneNumber
            )
            .map((additionalPhone) => (
              <MenuItem
                value={additionalPhone.info?.phone}
                key={additionalPhone.id}>
                {additionalPhone.info?.phone}
              </MenuItem>
            ))}
          <Box display="flex" justifyContent="center" marginTop={1}>
            <Button
              onClick={() => {
                setOpenAddPhoneNumberDialog(true)
              }}
              variant={'text'}>
              Add a New Phone Number
            </Button>
          </Box>
        </Select>
      </FormControl>
    </>
  )
}
